import { useState, useEffect, useMemo, forwardRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  ButtonBase,
  Collapse,
  SxProps,
  Theme,
} from "@mui/material";
import { styled, useTheme, keyframes } from "@mui/material/styles";
import {
  IconBarbell,
  IconHistory,
  IconMapRoute,
  IconHeartHandshake,
  IconHelp,
  IconPennant2,
} from "@tabler/icons-react";
import {
  orange,
  cyan,
  green,
  pink,
  blue,
  grey,
  red,
} from "@mui/material/colors";
import useCustomNavigation from "hooks/customNavigation";
import CurrentSession from "data/userSession";
import { useLayout } from "./LayoutContext";

// Tab configuration
export interface TabConfig {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  path: string;
}

const COMMUNITY_TAB: TabConfig = {
  id: "community",
  label: "Community",
  icon: IconHeartHandshake,
  color: pink[500],
  path: "/feed",
};

const MILESTONES_TAB: TabConfig = {
  id: "photos",
  label: "Photos",
  icon: IconPennant2,
  color: red[500],
  path: "/photos",
};

const buildTabConfig = (isIncognito: boolean): TabConfig[] =>
  [
    {
      id: "tug",
      label: "Tug",
      icon: IconBarbell,
      color: orange[500],
      path: "/tug",
    },
    {
      id: "history",
      label: "History",
      icon: IconHistory,
      color: cyan[500],
      path: "/history",
    },
    {
      id: "progress",
      label: "Progress",
      icon: IconMapRoute,
      color: green[500],
      path: "/progress",
    },
    // Photos always shows; Community is hidden in incognito mode.
    MILESTONES_TAB,
    isIncognito ? null : COMMUNITY_TAB,
    {
      id: "directory",
      label: "Help",
      icon: IconHelp,
      color: blue[500],
      path: "/directory",
    },
  ].filter((tab): tab is TabConfig => tab !== null);

export const TAB_CONFIG = buildTabConfig(false);

// Keyframe animations
const scaleUp = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1.1); }
`;

const scaleDown = keyframes`
  from { transform: scale(1.1); }
  to { transform: scale(1); }
`;

// Ripple animation for active session indicator
const ripple = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

// Styled components - now uses static positioning within flex layout
const TabBarContainer = styled(Paper)(({ theme }) => ({
  flexShrink: 0, // Don't shrink in flex container
  borderRadius: "16px 16px 0 0",
  overflow: "hidden",
  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
}));

const TabBarContent = styled(Box)({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  padding: "8px 4px",
});

interface TabButtonProps {
  isActive: boolean;
  tabColor: string;
}

const TabButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "isActive" && prop !== "tabColor",
})<TabButtonProps>(({ isActive, tabColor }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  padding: isActive ? "10px 16px" : "10px 12px",
  borderRadius: "24px",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: isActive ? `${tabColor}20` : "transparent",
  color: isActive ? tabColor : grey[500],
  // Reliable taps on mobile: skip the tap delay/gesture ambiguity...
  touchAction: "manipulation",
  WebkitTapHighlightColor: "transparent",
  "&:hover": {
    backgroundColor: isActive ? `${tabColor}25` : grey[100],
  },
  // NOTE: deliberately no `transform: scale()` on :active. Shrinking the button
  // under the finger between pointerdown and pointerup can move the release point
  // outside the smaller hit area, canceling the click so the tab never switches.
  // Press feedback comes from the background tint above instead.
}));

interface IconContainerProps {
  isActive: boolean;
}

const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<IconContainerProps>(({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  transform: isActive ? "scale(1.1)" : "scale(1)",
  animation: isActive
    ? `${scaleUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)`
    : `${scaleDown} 0.2s ease-out`,
}));

const LabelText = styled("span")(() => ({
  fontSize: "13px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  transition: "opacity 0.15s ease-in-out",
}));

// Active session indicator component
const ActiveSessionIndicator = () => (
  <Box
    sx={{
      position: "absolute",
      top: -2,
      right: -4,
      width: 12,
      height: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {/* Ripple effect */}
    <Box
      sx={{
        position: "absolute",
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: green[500],
        animation: `${ripple} 2s ease-out infinite`,
      }}
    />
    {/* Solid dot */}
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: green[500],
        border: "1.5px solid white",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
      }}
    />
  </Box>
);

// Helper to determine active tab from path
const getActiveTabFromPath = (
  pathname: string,
  tabs: TabConfig[]
): string | null => {
  // Check for exact matches first
  const exactMatch = tabs.find((tab) => tab.path === pathname);
  if (exactMatch) return exactMatch.id;

  // Check for prefix matches (e.g., /community/123 should match /community)
  const prefixMatch = tabs.find(
    (tab) => pathname.startsWith(tab.path) && tab.path !== "/"
  );
  if (prefixMatch) return prefixMatch.id;

  return null;
};

interface BottomTabBarProps {
  className?: string;
  sx?: SxProps<Theme>;
}

const BottomTabBar = forwardRef<HTMLDivElement, BottomTabBarProps>(
  ({ className, sx }, ref) => {
    const theme = useTheme();
    const navigate = useCustomNavigation();
    const location = useLocation();
    const { hasActiveSession } = useLayout();

    const [isIncognito, setIsIncognito] = useState(false);
    // Stable reference so effects/memos keyed on `tabs` don't churn every render.
    const tabs = useMemo(() => buildTabConfig(isIncognito), [isIncognito]);

    // Derive the active tab straight from the URL -- the route is the single
    // source of truth. No optimistic state to get stuck on the wrong tab if a
    // navigation is superseded; it self-heals to wherever the router actually is.
    const activeTab = getActiveTabFromPath(location.pathname, tabs);

    useEffect(() => {
      CurrentSession.load().then((user) => {
        setIsIncognito(user.incognito ?? false);
      });
    }, []);

    const handleTabClick = (tab: TabConfig) => {
      navigate(tab.path.replace("/", "")); // Remove leading slash for customNavigation
    };

    return (
      <TabBarContainer
        ref={ref}
        className={className}
        elevation={8}
        sx={{
          backgroundColor: theme.palette.background.paper,
          ...sx,
        }}
      >
        <TabBarContent>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;
            const showActiveIndicator = tab.id === "tug" && hasActiveSession;

            return (
              <TabButton
                key={tab.id}
                isActive={isActive}
                tabColor={tab.color}
                onClick={() => handleTabClick(tab)}
              >
                <IconContainer isActive={isActive}>
                  <Icon
                    size={24}
                    stroke={isActive ? 2.5 : 2}
                    color={isActive ? tab.color : grey[500]}
                  />
                  {showActiveIndicator && <ActiveSessionIndicator />}
                </IconContainer>
                <Collapse
                  in={isActive}
                  orientation="horizontal"
                  timeout={200}
                  unmountOnExit
                >
                  <LabelText style={{ color: tab.color }}>
                    {tab.label}
                  </LabelText>
                </Collapse>
              </TabButton>
            );
          })}
        </TabBarContent>
      </TabBarContainer>
    );
  }
);

export default BottomTabBar;
